// components/CreateVisitModal.jsx - Complete with conditional fields
import React, { useState, useEffect } from "react";
import { X, Search, Building2, User as UserIcon } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreateVisitModal = ({ account, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [visitType, setVisitType] = useState("technical");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [accounts, setAccounts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);

  const [formData, setFormData] = useState({
    // Common fields
    accountId: account?.id || "",
    visitType: "technical",
    visitDate: "",
    visitDateTime: "",
    status: "scheduled",
    ownerId: "",
    contactId: "",
    street: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",

    // Technical Visit Specific
    engineerId: "",
    jobDetails: "",
    routerIpAddress: "",
    routerPassword: "",
    routerType: "",
    broadbandType: "",
    broadbandSpeed: "",
    callSwitchCommunicatorAppInstalled: false,
    fullTrainingProvided: false,
    allHardwareInstalled: false,
    existingPhoneEquipmentLeft: false,
    divertApplied: false,
    callRecordingRegistered: false,
    callRecordingNotes: "",
    outstandingWork: "",
    workNotes: "",

    // Sales Visit Specific
    salesMemberId: "",
    opportunityId: "",
    outcome: "",
    vsistNotes: "", // Typo matches your model
  });

  useEffect(() => {
    fetchAccounts();
    fetchUsers();
    if (account?.id) {
      fetchContacts(account.id);
      fetchOpportunities(account.id);
    }
  }, [account]);

  const fetchAccounts = async () => {
    try {
      const response = await apiService.getAccounts();
      setAccounts(response.data || []);
    } catch (err) {
      console.error("Error fetching accounts:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      setUsers(response.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchContacts = async (accountId) => {
    try {
      const response = await apiService.getContacts({ accountId });
      setContacts(response.data || []);
    } catch (err) {
      console.error("Error fetching contacts:", err);
    }
  };

  const fetchOpportunities = async (accountId) => {
    try {
      const response = await apiService.getOpportunities({ accountId });
      setOpportunities(response.data || []);
    } catch (err) {
      console.error("Error fetching opportunities:", err);
    }
  };

  const handleAccountChange = (accountId) => {
    setFormData({
      ...formData,
      accountId,
      contactId: "",
      opportunityId: "",
    });
    if (accountId) {
      fetchContacts(accountId);
      fetchOpportunities(accountId);
    } else {
      setContacts([]);
      setOpportunities([]);
    }
  };

  const handleVisitTypeChange = (type) => {
    setVisitType(type);
    setFormData({
      ...formData,
      visitType: type,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Clean data based on visit type
      const submitData = { ...formData };

      // Helper function to clean empty UUID fields
      const cleanUuidField = (value) => {
        return value && value.trim() !== "" ? value : null;
      };

      // Clean UUID fields - convert empty strings to null
      submitData.ownerId = cleanUuidField(submitData.ownerId);
      submitData.contactId = cleanUuidField(submitData.contactId);
      submitData.engineerId = cleanUuidField(submitData.engineerId);
      submitData.salesMemberId = cleanUuidField(submitData.salesMemberId);
      submitData.opportunityId = cleanUuidField(submitData.opportunityId);

      if (visitType === "sales") {
        // Remove technical fields
        delete submitData.engineerId;
        delete submitData.jobDetails;
        delete submitData.routerIpAddress;
        delete submitData.routerPassword;
        delete submitData.routerType;
        delete submitData.broadbandType;
        delete submitData.broadbandSpeed;
        delete submitData.callSwitchCommunicatorAppInstalled;
        delete submitData.fullTrainingProvided;
        delete submitData.allHardwareInstalled;
        delete submitData.existingPhoneEquipmentLeft;
        delete submitData.divertApplied;
        delete submitData.callRecordingRegistered;
        delete submitData.callRecordingNotes;
        delete submitData.outstandingWork;
        delete submitData.workNotes;
      } else {
        // Remove sales fields
        delete submitData.salesMemberId;
        delete submitData.opportunityId;
        delete submitData.outcome;
        delete submitData.vsistNotes;
      }

      await apiService.createVisit(submitData);
      onSuccess();
    } catch (err) {
      console.error("Error creating visit:", err);
      setError(err.response?.data?.message || "Failed to create visit");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border border-gray-700"
            : "bg-white border border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h2
            className={`text-lg font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            New Visit
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div
              className={`mb-4 p-3 rounded-lg text-sm ${
                isDarkMode
                  ? "bg-red-500/20 border border-red-500/30 text-red-400"
                  : "bg-red-50 border border-red-200 text-red-600"
              }`}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} id="visitForm">
            {/* Visit Type Selector */}
            <div className="mb-6">
              <label
                className={`block text-sm font-medium mb-3 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Visit Type *
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleVisitTypeChange("technical")}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    visitType === "technical"
                      ? isDarkMode
                        ? "border-purple-500 bg-purple-500/20"
                        : "border-purple-500 bg-purple-50"
                      : isDarkMode
                        ? "border-gray-700 bg-[#0C0E12] hover:border-gray-600"
                        : "border-gray-300 bg-white hover:border-gray-400"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">🔧</div>
                    <div
                      className={`font-semibold ${
                        visitType === "technical"
                          ? isDarkMode
                            ? "text-purple-400"
                            : "text-purple-600"
                          : isDarkMode
                            ? "text-gray-400"
                            : "text-gray-500"
                      }`}
                    >
                      Technical Visit
                    </div>
                    <div
                      className={`text-xs mt-1 ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Installation, support, maintenance
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleVisitTypeChange("sales")}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    visitType === "sales"
                      ? isDarkMode
                        ? "border-green-500 bg-green-500/20"
                        : "border-green-500 bg-green-50"
                      : isDarkMode
                        ? "border-gray-700 bg-[#0C0E12] hover:border-gray-600"
                        : "border-gray-300 bg-white hover:border-gray-400"
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">📈</div>
                    <div
                      className={`font-semibold ${
                        visitType === "sales"
                          ? isDarkMode
                            ? "text-green-400"
                            : "text-green-600"
                          : isDarkMode
                            ? "text-gray-400"
                            : "text-gray-500"
                      }`}
                    >
                      Sales Visit
                    </div>
                    <div
                      className={`text-xs mt-1 ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Meetings, demos, follow-ups
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Common Information */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Visit Information
              </h3>
            </div>

            <div className="space-y-4 mb-6">
              {/* Account */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  <span className="text-red-500">*</span> Account
                </label>
                <div className="col-span-2">
                  {account ? (
                    <div
                      className={`flex items-center gap-2 px-3 py-2 border rounded ${
                        isDarkMode
                          ? "bg-blue-500/20 border-blue-500/30"
                          : "bg-blue-50 border-blue-200"
                      }`}
                    >
                      <Building2
                        className={`w-4 h-4 ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}
                      />
                      <span
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {account.name}
                      </span>
                    </div>
                  ) : (
                    <select
                      value={formData.accountId}
                      onChange={(e) => handleAccountChange(e.target.value)}
                      required
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-[#0C0E12] border-gray-700 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select Account</option>
                      {accounts.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Contact */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Contact
                </label>
                <div className="col-span-2">
                  <select
                    value={formData.contactId}
                    onChange={(e) => handleChange("contactId", e.target.value)}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select Contact</option>
                    {contacts.map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.firstName} {contact.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Visit Date */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  <span className="text-red-500">*</span> Visit Date
                </label>
                <div className="col-span-2">
                  <input
                    type="date"
                    value={formData.visitDate}
                    onChange={(e) => handleChange("visitDate", e.target.value)}
                    required
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              {/* Visit Date Time */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  <span className="text-red-500">*</span> Visit Date & Time
                </label>
                <div className="col-span-2">
                  <input
                    type="datetime-local"
                    value={formData.visitDateTime}
                    onChange={(e) =>
                      handleChange("visitDateTime", e.target.value)
                    }
                    required
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              {/* Status */}
              <div className="grid grid-cols-3 gap-4 items-start">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Status
                </label>
                <div className="col-span-2">
                  <select
                    value={formData.status}
                    onChange={(e) => handleChange("status", e.target.value)}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="no_show">No Show</option>
                  </select>
                </div>
              </div>
            </div>

            {/* TECHNICAL VISIT FIELDS */}
            {visitType === "technical" && (
              <>
                <div
                  className={`px-4 py-2 mb-4 rounded ${
                    isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                  }`}
                >
                  <h3
                    className={`text-sm font-semibold ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Technical Visit Details
                  </h3>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Engineer */}
                  <div className="grid grid-cols-3 gap-4 items-start">
                    <label
                      className={`text-sm pt-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Engineer
                    </label>
                    <div className="col-span-2">
                      <select
                        value={formData.engineerId}
                        onChange={(e) =>
                          handleChange("engineerId", e.target.value)
                        }
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        <option value="">Select Engineer</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Job Details */}
                  <div className="grid grid-cols-3 gap-4 items-start">
                    <label
                      className={`text-sm pt-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Job Details
                    </label>
                    <div className="col-span-2">
                      <textarea
                        rows="3"
                        value={formData.jobDetails}
                        onChange={(e) =>
                          handleChange("jobDetails", e.target.value)
                        }
                        placeholder="Installation of 3 Cordless with Base, Users and programming done, Divert done, Router configured"
                        className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {/* Work Information */}
                <div
                  className={`px-4 py-2 mb-4 rounded ${
                    isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                  }`}
                >
                  <h3
                    className={`text-sm font-semibold ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Work Information
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-x-8 gap-y-4 mb-6">
                  {/* Router IP Address */}
                  <div className="grid grid-cols-3 gap-4 items-start">
                    <label
                      className={`text-sm pt-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Router IP Address
                    </label>
                    <div className="col-span-2">
                      <input
                        type="text"
                        value={formData.routerIpAddress}
                        onChange={(e) =>
                          handleChange("routerIpAddress", e.target.value)
                        }
                        placeholder="192.168.1.1"
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                        }`}
                      />
                    </div>
                  </div>

                  {/* Router Password */}
                  <div className="grid grid-cols-3 gap-4 items-start">
                    <label
                      className={`text-sm pt-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Router Password
                    </label>
                    <div className="col-span-2">
                      <input
                        type="text"
                        value={formData.routerPassword}
                        onChange={(e) =>
                          handleChange("routerPassword", e.target.value)
                        }
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      />
                    </div>
                  </div>

                  {/* Router Type */}
                  <div className="grid grid-cols-3 gap-4 items-start">
                    <label
                      className={`text-sm pt-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Router Type
                    </label>
                    <div className="col-span-2">
                      <input
                        type="text"
                        value={formData.routerType}
                        onChange={(e) =>
                          handleChange("routerType", e.target.value)
                        }
                        placeholder="TP-Link Archer"
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                        }`}
                      />
                    </div>
                  </div>

                  {/* Broadband Type */}
                  <div className="grid grid-cols-3 gap-4 items-start">
                    <label
                      className={`text-sm pt-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Broadband Type
                    </label>
                    <div className="col-span-2">
                      <input
                        type="text"
                        value={formData.broadbandType}
                        onChange={(e) =>
                          handleChange("broadbandType", e.target.value)
                        }
                        placeholder="SOGEA"
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                        }`}
                      />
                    </div>
                  </div>

                  {/* Broadband Speed */}
                  <div className="grid grid-cols-3 gap-4 items-start">
                    <label
                      className={`text-sm pt-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Broadband Speed
                    </label>
                    <div className="col-span-2">
                      <input
                        type="text"
                        value={formData.broadbandSpeed}
                        onChange={(e) =>
                          handleChange("broadbandSpeed", e.target.value)
                        }
                        placeholder="24/17"
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {/* Checkboxes */}
                <div
                  className={`px-4 py-2 mb-4 rounded ${
                    isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                  }`}
                >
                  <h3
                    className={`text-sm font-semibold ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Installation Checklist
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <label
                    className={`flex items-center gap-2 text-sm cursor-pointer ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.callSwitchCommunicatorAppInstalled}
                      onChange={(e) =>
                        handleChange(
                          "callSwitchCommunicatorAppInstalled",
                          e.target.checked,
                        )
                      }
                      className={`w-4 h-4 rounded border focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-700 bg-[#0C0E12] text-blue-600"
                          : "border-gray-300 bg-white text-blue-600"
                      }`}
                    />
                    Call Switch Communicator App Installed
                  </label>

                  <label
                    className={`flex items-center gap-2 text-sm cursor-pointer ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.fullTrainingProvided}
                      onChange={(e) =>
                        handleChange("fullTrainingProvided", e.target.checked)
                      }
                      className={`w-4 h-4 rounded border focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-700 bg-[#0C0E12] text-blue-600"
                          : "border-gray-300 bg-white text-blue-600"
                      }`}
                    />
                    Full Training Provided
                  </label>

                  <label
                    className={`flex items-center gap-2 text-sm cursor-pointer ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.allHardwareInstalled}
                      onChange={(e) =>
                        handleChange("allHardwareInstalled", e.target.checked)
                      }
                      className={`w-4 h-4 rounded border focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-700 bg-[#0C0E12] text-blue-600"
                          : "border-gray-300 bg-white text-blue-600"
                      }`}
                    />
                    All Hardware Installed
                  </label>

                  <label
                    className={`flex items-center gap-2 text-sm cursor-pointer ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.existingPhoneEquipmentLeft}
                      onChange={(e) =>
                        handleChange(
                          "existingPhoneEquipmentLeft",
                          e.target.checked,
                        )
                      }
                      className={`w-4 h-4 rounded border focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-700 bg-[#0C0E12] text-blue-600"
                          : "border-gray-300 bg-white text-blue-600"
                      }`}
                    />
                    Existing Phone Equipment Left?
                  </label>

                  <label
                    className={`flex items-center gap-2 text-sm cursor-pointer ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.divertApplied}
                      onChange={(e) =>
                        handleChange("divertApplied", e.target.checked)
                      }
                      className={`w-4 h-4 rounded border focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-700 bg-[#0C0E12] text-blue-600"
                          : "border-gray-300 bg-white text-blue-600"
                      }`}
                    />
                    Divert Applied
                  </label>

                  <label
                    className={`flex items-center gap-2 text-sm cursor-pointer ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.callRecordingRegistered}
                      onChange={(e) =>
                        handleChange(
                          "callRecordingRegistered",
                          e.target.checked,
                        )
                      }
                      className={`w-4 h-4 rounded border focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "border-gray-700 bg-[#0C0E12] text-blue-600"
                          : "border-gray-300 bg-white text-blue-600"
                      }`}
                    />
                    Call Recording Registered?
                  </label>
                </div>

                {/* Additional Notes */}
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-3 gap-4 items-start">
                    <label
                      className={`text-sm pt-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Call Recording Notes
                    </label>
                    <div className="col-span-2">
                      <textarea
                        rows="2"
                        value={formData.callRecordingNotes}
                        onChange={(e) =>
                          handleChange("callRecordingNotes", e.target.value)
                        }
                        className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 items-start">
                    <label
                      className={`text-sm pt-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Outstanding Work
                    </label>
                    <div className="col-span-2">
                      <textarea
                        rows="3"
                        value={formData.outstandingWork}
                        onChange={(e) =>
                          handleChange("outstandingWork", e.target.value)
                        }
                        placeholder="Port of Number: 01388811468&#10;Port of Number: 01388811438 (After our BB is live and set-up ATA)&#10;Will need a switch and Cisco ATA sending out BB to be installed (Router configured)"
                        className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                        }`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 items-start">
                    <label
                      className={`text-sm pt-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Notes
                    </label>
                    <div className="col-span-2">
                      <textarea
                        rows="3"
                        value={formData.workNotes}
                        onChange={(e) =>
                          handleChange("workNotes", e.target.value)
                        }
                        className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* SALES VISIT FIELDS */}
            {visitType === "sales" && (
              <>
                <div
                  className={`px-4 py-2 mb-4 rounded ${
                    isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                  }`}
                >
                  <h3
                    className={`text-sm font-semibold ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Sales Visit Details
                  </h3>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Sales Member */}
                  <div className="grid grid-cols-3 gap-4 items-start">
                    <label
                      className={`text-sm pt-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Sales Member
                    </label>
                    <div className="col-span-2">
                      <select
                        value={formData.salesMemberId}
                        onChange={(e) =>
                          handleChange("salesMemberId", e.target.value)
                        }
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        <option value="">Select Sales Member</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Opportunity */}
                  <div className="grid grid-cols-3 gap-4 items-start">
                    <label
                      className={`text-sm pt-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Opportunity
                    </label>
                    <div className="col-span-2">
                      <select
                        value={formData.opportunityId}
                        onChange={(e) =>
                          handleChange("opportunityId", e.target.value)
                        }
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        <option value="">Select Opportunity</option>
                        {opportunities.map((opp) => (
                          <option key={opp.id} value={opp.id}>
                            {opp.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Outcome */}
                  <div className="grid grid-cols-3 gap-4 items-start">
                    <label
                      className={`text-sm pt-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Outcome
                    </label>
                    <div className="col-span-2">
                      <select
                        value={formData.outcome}
                        onChange={(e) =>
                          handleChange("outcome", e.target.value)
                        }
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        <option value="">--None--</option>
                        <option value="QUAL">QUAL</option>
                        <option value="NON QUAL">NON QUAL</option>
                        <option value="SAT">SAT</option>
                        <option value="NON SAT">NON SAT</option>
                        <option value="SOLD">SOLD</option>
                        <option value="NOT INTERESTED">NOT INTERESTED</option>
                      </select>
                    </div>
                  </div>

                  {/* Visit Notes */}
                  <div className="grid grid-cols-3 gap-4 items-start">
                    <label
                      className={`text-sm pt-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Visit Notes
                    </label>
                    <div className="col-span-2">
                      <textarea
                        rows="4"
                        value={formData.vsistNotes}
                        onChange={(e) =>
                          handleChange("vsistNotes", e.target.value)
                        }
                        placeholder="Sat with Paul six months into contract with BT one phone so 4 1/2 years left on contract quote, but he needs to confirm with his wife"
                        className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                        }`}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Address Information (Common) */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Location Information
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div className="grid grid-cols-3 gap-4 items-start col-span-2">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Street
                </label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.street}
                    onChange={(e) => handleChange("street", e.target.value)}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 items-start">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  City
                </label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 items-start">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  State/Province
                </label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleChange("state", e.target.value)}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 items-start">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Postal Code
                </label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => handleChange("postalCode", e.target.value)}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 items-start">
                <label
                  className={`text-sm pt-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Country
                </label>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => handleChange("country", e.target.value)}
                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="visitForm"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateVisitModal;
